import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Stack,
  IconButton,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QuizIcon from '@mui/icons-material/Quiz';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';

// Components (you'll need to create these)
import QuizSession from '../../components/study-content/QuizSession';
import FlashcardSession from '../../components/study-content/FlashcardSession';
import PracticeSession from '../../components/study-content/PracticeSession';

// Types
interface StudySession {
  id: string;
  title: string;
  description: string;
  type: 'quiz' | 'flashcards' | 'practice';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: number;
  questions?: any[];
  content?: any[];
}

// Styled Components
const SessionHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SessionContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(4),
}));

const StudyContentSession: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<StudySession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Mock data fetch - replace with actual API call
    const fetchSession = async () => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mock session data
        const mockSession: StudySession = {
          id: sessionId || '',
          title: getSessionTitle(sessionId || ''),
          description: getSessionDescription(sessionId || ''),
          type: getSessionType(sessionId || ''),
          difficulty: 'intermediate',
          estimatedTime: 15,
          questions: generateMockQuestions(),
        };
        
        setSession(mockSession);
      } catch (error) {
        console.error('Failed to fetch session:', error);
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      fetchSession();
    }
  }, [sessionId]);

  const getSessionTitle = (id: string): string => {
    if (id.includes('vocab')) return 'Vocabulary Quiz';
    if (id.includes('grammar')) return 'Grammar Flashcards';
    if (id.includes('listening')) return 'Listening Comprehension';
    if (id.includes('reading')) return 'Reading Comprehension Quiz';
    return 'Study Session';
  };

  const getSessionDescription = (id: string): string => {
    if (id.includes('vocab')) return 'Test your knowledge of this week\'s vocabulary words';
    if (id.includes('grammar')) return 'Practice grammar concepts with interactive flashcards';
    if (id.includes('listening')) return 'Improve your listening skills with audio exercises';
    if (id.includes('reading')) return 'Test your understanding of reading passages';
    return 'Interactive study session';
  };

  const getSessionType = (id: string): 'quiz' | 'flashcards' | 'practice' => {
    if (id.includes('flashcards')) return 'flashcards';
    if (id.includes('practice')) return 'practice';
    return 'quiz';
  };

  const generateMockQuestions = () => {
    return [
      { id: 1, question: 'What does "comprehensive" mean?', options: ['Complete', 'Simple', 'Fast', 'Easy'], correct: 0 },
      { id: 2, question: 'Choose the correct grammar:', options: ['I am going', 'I are going', 'I is going', 'I be going'], correct: 0 },
      { id: 3, question: 'Which is a synonym for "difficult"?', options: ['Easy', 'Hard', 'Simple', 'Quick'], correct: 1 },
    ];
  };

  const getSessionIcon = (type: string) => {
    switch (type) {
      case 'quiz':
        return <QuizIcon sx={{ fontSize: 24 }} />;
      case 'flashcards':
        return <StarIcon sx={{ fontSize: 24 }} />;
      case 'practice':
        return <TrendingUpIcon sx={{ fontSize: 24 }} />;
      default:
        return <QuizIcon sx={{ fontSize: 24 }} />;
    }
  };

  const handleBack = () => {
    navigate('/student/my-classroom');
  };

  const handleStartSession = () => {
    setSessionStarted(true);
  };

  const handleSessionComplete = (results: any) => {
    console.log('Session completed with results:', results);
    // Handle session completion - save results, update progress, etc.
    navigate('/student/my-classroom', { 
      state: { 
        sessionCompleted: true, 
        sessionResults: results 
      } 
    });
  };

  const handleProgressUpdate = (newProgress: number) => {
    setProgress(newProgress);
  };

  if (loading) {
    return (
      <SessionContainer maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Loading session...</Typography>
        </Box>
        <LinearProgress />
      </SessionContainer>
    );
  }

  if (!session) {
    return (
      <SessionContainer maxWidth="lg">
        <Alert severity="error" sx={{ mb: 3 }}>
          Session not found. Please try again.
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back to My Classroom
        </Button>
      </SessionContainer>
    );
  }

  return (
    <SessionContainer maxWidth="lg">
      {/* Header */}
      <SessionHeader>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {session.title}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip 
                label={session.type} 
                color="primary" 
                variant="outlined" 
                icon={getSessionIcon(session.type)}
              />
              <Chip label={session.difficulty} size="small" />
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TimerIcon sx={{ fontSize: 16 }} color="action" />
                <Typography variant="body2" color="text.secondary">
                  ~{session.estimatedTime} min
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {session.description}
        </Typography>

        {sessionStarted && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        )}
      </SessionHeader>

      {/* Session Content */}
      {!sessionStarted ? (
        // Session Start Screen
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'primary.light',
                  color: 'primary.main',
                  mb: 2,
                }}
              >
                {getSessionIcon(session.type)}
              </Box>
              <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                Ready to Start?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                This session contains {session.questions?.length || 0} questions and should take approximately {session.estimatedTime} minutes to complete.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Session Tips
              </Typography>
              <Stack spacing={1} sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
                <Typography variant="body2" color="text.secondary">
                  • Take your time to read each question carefully
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • You can review your answers before submitting
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Your progress is saved automatically
                </Typography>
              </Stack>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleStartSession}
              sx={{ px: 4, py: 1.5 }}
            >
              Start Session
            </Button>
          </CardContent>
        </Card>
      ) : (
        // Active Session Content
        <Box>
          {session.type === 'quiz' && (
            <QuizSession
              questions={session.questions || []}
              onComplete={handleSessionComplete}
              onProgressUpdate={handleProgressUpdate}
            />
          )}
          {session.type === 'flashcards' && (
            <FlashcardSession
              content={session.content || []}
              onComplete={handleSessionComplete}
              onProgressUpdate={handleProgressUpdate}
            />
          )}
          {session.type === 'practice' && (
            <PracticeSession
              content={session.content || []}
              onComplete={handleSessionComplete}
              onProgressUpdate={handleProgressUpdate}
            />
          )}
        </Box>
      )}
    </SessionContainer>
  );
};

export default StudyContentSession;